import { get } from 'lodash';
import { REHYDRATE } from 'redux-persist/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import {
  addLocale,
  IsExternalUrl,
  removeLocale,
  replaceParams
} from '@epi-helpers/UrlHelpers';
import { routerPaths } from '@epi-constants/routerPaths';

import history from '../../routes/history';

export type NavigationState = {
  locationHistory: string[];
  rehydrated?: boolean;
};

export const getInitialState = (): NavigationState => ({
  locationHistory: []
});

const initialState = getInitialState();

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    addLocationHistory: (state, { payload: path }: PayloadAction<string>) => {
      state.locationHistory = [path, ...state.locationHistory].slice(0, 15);
    },
    deleteLastLocationHistory: (
      state,
      { payload: entries }: PayloadAction<number>
    ) => {
      state.locationHistory = state.locationHistory.slice(entries);
    }
  },
  extraReducers: builder => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      if (action.payload?.navigation) {
        return {
          ...state,
          ...action.payload.navigation,
          rehydrated: true
        };
      }
      return state;
    });
  }
});

export const { reducer, actions: navigationActions } = navigationSlice;

type GoToPageOptions = {
  target?: string;
  params?: Record<string, string>;
};

export const goToPage = (
  url: string,
  { target, params }: GoToPageOptions = {}
) => ({
  type: 'navigation/goToPage',
  url,
  target,
  params
});

export const replacePage = (url: string) => ({
  type: 'navigation/replacePage',
  url
});

export const goToReturnPage = ({
  sessionExpired
}: { sessionExpired?: boolean } = {}) => ({
  type: 'navigation/goToReturnPage',
  sessionExpired: !!sessionExpired
});

export const saveLocationHistory = (currentLocation?: {
  pathname?: string;
}) => ({
  type: 'navigation/saveLocationHistory',
  currentLocation
});

const navigateToUrl = (
  url: string,
  { target, params = {} }: GoToPageOptions = {},
  getState
) => {
  if (target && IsExternalUrl(url)) {
    window.open(url, target);
    return;
  }

  if (IsExternalUrl(url)) {
    window.location.href = url;
    return;
  }

  const parameterizedUrl = replaceParams(url, params);
  const languageId = getApplicationLanguageId(getState());
  const localizedUrl = addLocale(languageId, parameterizedUrl);
  const currentUrl = get(history, 'location.pathname') || '';

  if (localizedUrl !== currentUrl) {
    history.push(localizedUrl);
  }
};

const replaceUrl = (url: string, getState) => {
  const languageId = getApplicationLanguageId(getState());
  const localizedUrl = addLocale(languageId, url);
  history.replace(localizedUrl);
};

export const navigationMiddleware = store => next => action => {
  const result = next(action);

  switch (action.type) {
    case 'navigation/goToPage':
      navigateToUrl(
        action.url,
        { target: action.target, params: action.params },
        store.getState
      );
      break;
    case 'navigation/replacePage':
      replaceUrl(action.url, store.getState);
      break;
    case 'navigation/goToReturnPage': {
      const routesToSkip = [routerPaths.login, routerPaths.newPassword];
      const lastVisitedPages: string[] =
        store.getState().navigation.locationHistory;

      const index = [...lastVisitedPages]
        .splice(1)
        .findIndex(path => !routesToSkip.some(route => path.endsWith(route)));

      if (index !== -1) {
        store.dispatch(navigationActions.deleteLastLocationHistory(index + 1));
      }

      const returnUrl = index === -1 ? '' : lastVisitedPages[index + 1];
      store.dispatch(
        goToPage(action.sessionExpired ? routerPaths.login : returnUrl || '/')
      );
      break;
    }
    case 'navigation/saveLocationHistory': {
      const lastSavedLocation = store.getState().navigation.locationHistory[0];
      const path = removeLocale(action.currentLocation?.pathname || '');

      if (path && lastSavedLocation !== path) {
        store.dispatch(navigationActions.addLocationHistory(path));
      }
      break;
    }
    default:
      break;
  }

  return result;
};
